import { FC } from 'react';
import { Box, CircularProgress } from '@mui/material';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';

import { ScenarioUnifiedMitigation, UnifiedMitigation } from '../../../../../types';

import UnifiedMitigationItem from './UnifiedMitigationItem';

interface ScenarioUnifiedMitigationsListProps {
  items: ScenarioUnifiedMitigation[];
  setItems: (items: ScenarioUnifiedMitigation[]) => void;
  openDialog: (mitigation: UnifiedMitigation) => void;
  persistOrder: (items: ScenarioUnifiedMitigation[]) => void;
  typeId?: string;
  isLoading: boolean;
}

const ScenarioUnifiedMitigationsList: FC<ScenarioUnifiedMitigationsListProps> = ({
  items,
  setItems,
  openDialog,
  persistOrder,
  typeId,
  isLoading = false
}) => {
  const dropAreaName = typeId ? `mitigation-${typeId}` : 'mitigation';
  const [, drop] = useDrop({
    accept: dropAreaName
  });

  const findMitigation = (id: string) => {
    const index = items.findIndex(m => m.unifiedmitigation === id);
    if (index === -1) {
      throw new Error(`Mitigation with id ${id} not found in list`);
    }
    return { index };
  };

  const moveMitigation = (id: string, atIndex: number, mutate?: boolean) => {
    const { index } = findMitigation(id);
    const item = items[index];

    const nextItems = update(items, {
      $splice: [
        [index, 1],
        [atIndex, 0, item]
      ]
    });

    setItems(nextItems);

    if (mutate) {
      persistOrder(nextItems);
    }
  };

  const onDeletedMitigation = (scenarioUnifiedMitigation: string) => {
    const newItems = items.filter(item => item.id !== scenarioUnifiedMitigation);
    persistOrder(newItems);
  };

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
          <CircularProgress />
        </Box>
      ) : (
        <Box component="ol" ref={drop} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map(item => (
            <UnifiedMitigationItem
              key={item.unifiedmitigation}
              id={item.unifiedmitigation}
              scenarioUnifiedMitigation={item.id}
              findMitigation={findMitigation}
              moveMitigation={moveMitigation}
              openMitigationDialog={openDialog}
              onDeletedMitigation={onDeletedMitigation}
              dropAreaName={dropAreaName}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default ScenarioUnifiedMitigationsList;
